import React from 'react';
import { Box, Paper, Typography, TextField, Button } from '@mui/material';
import { compactInputSx } from '../../styles/wizardStyles';

/**
 * Left-column panel listing tournament rounds with date pickers.
 * Used by both StepNormalRoundsMatches and StepTeamRoundsMatches.
 *
 * Props:
 *  - rounds: { ron_id, ron_numero, ron_fecha }[]
 *  - activeRoundId: number
 *  - onRoundSelect: (ronId: number) => void
 *  - onRoundDateChange: (ronId: number, date: string) => void
 *  - onAddRound: () => void
 *  - minHeight: string (default '300px')
 *  - maxListHeight: string (default '250px')
 *  - addLabel: string (default '+ Agregar Ronda')
 */
export default function RoundsPanel({
  rounds,
  activeRoundId,
  onRoundSelect,
  onRoundDateChange,
  onAddRound,
  minHeight = '300px',
  maxListHeight = '250px',
  addLabel = '+ Agregar Ronda',
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        background: 'rgba(15, 23, 42, 0.5)',
        borderColor: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minHeight,
      }}
    >
      <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
        Rondas
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          maxHeight: maxListHeight,
          overflowY: 'auto',
          pr: 0.5,
        }}
      >
        {rounds.map((r) => {
          const isSelected = activeRoundId === r.ron_id;
          return (
            <Box
              key={r.ron_id}
              onClick={() => onRoundSelect(r.ron_id)}
              sx={{
                p: 1,
                px: 1.5,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                background: isSelected ? 'rgba(58, 110, 182, 0.15)' : 'rgba(255, 255, 255, 0.02)',
                border: isSelected
                  ? '1px solid #3a6eb6'
                  : '1px solid rgba(255, 255, 255, 0.05)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  background: isSelected
                    ? 'rgba(58, 110, 182, 0.2)'
                    : 'rgba(255, 255, 255, 0.05)',
                  borderColor: isSelected ? '#3a6eb6' : 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography variant="subtitle2" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
                Ronda {r.ron_numero}
              </Typography>
              <Box onClick={(e) => e.stopPropagation()}>
                <TextField
                  type="date"
                  label="Fecha"
                  size="small"
                  fullWidth
                  value={r.ron_fecha || ''}
                  onChange={(e) => onRoundDateChange(r.ron_id, e.target.value)}
                  InputLabelProps={{ shrink: true, style: { color: '#94a3b8' } }}
                  inputProps={{ style: { color: '#cbd5e1' } }}
                  sx={compactInputSx}
                />
              </Box>
            </Box>
          );
        })}
      </Box>

      <Button
        variant="outlined"
        fullWidth
        onClick={onAddRound}
        sx={{
          mt: 'auto',
          color: '#3a6eb6',
          borderColor: 'rgba(58, 110, 182, 0.3)',
          borderRadius: 0,
          '&:hover': {
            borderColor: '#3a6eb6',
            background: 'rgba(58, 110, 182, 0.05)',
          },
        }}
      >
        {addLabel}
      </Button>
    </Paper>
  );
}
